import { useState } from "react";
import { todayIn } from "../../../shared/dates";
import { formatTakenDate, yearsAgoLabel } from "../../../shared/photos";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { Icon } from "../../ui/Icon";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import type { WidgetDefinition, WidgetProps } from "../types";
import { usePhotoOfDay } from "./api";
import { PhotoEmpty } from "./PhotoFrameWidget";
import { PhotoMarkButtons } from "./PhotoMarks";
import { PhotoImage, useBoxSize, useStillPhotos } from "./PhotoView";
import "./photos.css";

interface PhotoDayConfig {
  album: string;
  showCaption: boolean;
}

function PhotoDay({ config, size, editing, fullscreen, openFullscreen }: WidgetProps<PhotoDayConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  return (
    <PhotoOfDayView
      date={todayIn(timeZone, now)}
      album={config.album.trim()}
      showCaption={config.showCaption}
      compact={size.variant === "compact"}
      editing={editing}
      fullscreen={fullscreen}
      openFullscreen={openFullscreen}
    />
  );
}

/** One day's photo (an "On this day" memory when there is one). Also used full-screen by Today. */
export function PhotoOfDayView({
  date,
  album,
  showCaption,
  compact = false,
  editing = false,
  fullscreen,
  openFullscreen,
}: {
  date: string;
  album: string;
  showCaption: boolean;
  compact?: boolean;
  editing?: boolean;
  fullscreen: boolean;
  openFullscreen: () => void;
}) {
  const q = usePhotoOfDay(date, album);
  const state = dataStateOf(q, 3 * 60 * 60_000);
  const still = useStillPhotos();
  const [failed, setFailed] = useState<string | null>(null);
  const [boxRef, box] = useBoxSize<HTMLDivElement>();

  if (!q.data) {
    if (state === "loading") return <div className="widget-loading" />;
    return <EmptyState icon={state === "offline" ? "wifiOff" : "alert"} title="Today’s photo is unavailable" detail="Retrying automatically." />;
  }
  const { photo, reason, yearsAgo } = q.data;
  if (!photo) return <PhotoEmpty album={album || undefined} />;

  const label = reason === "onThisDay" && yearsAgo ? yearsAgoLabel(yearsAgo) : "Photo of the day";
  const taken = formatTakenDate(photo.takenAt);
  const caption = showCaption ? photo.caption : null;

  return (
    <div ref={boxRef} className={`pf pf-day ${still ? "is-still" : ""} ${compact ? "is-compact" : ""}`}>
      <button className="pf-day-hit" onClick={fullscreen || editing ? undefined : openFullscreen} aria-label={fullscreen ? label : `${label}. Show full-screen`} disabled={editing}>
        <div className="pf-layer on" style={{ ["--fade" as string]: "0ms" }}>
          <PhotoImage
            photo={photo}
            box={box}
            still={still}
            // A slow, single drift: one long "breath" per day's photo.
            motion
            durationMs={60_000}
            onError={() => setFailed(photo.id)}
          />
        </div>
      </button>
      <span className={`pf-day-label ${reason === "onThisDay" ? "memory" : ""}`}>
        <Icon name={reason === "onThisDay" ? "sparkle" : "sun"} size="1rem" />
        {label}
      </span>
      {!editing && (
        <div className="pf-tools">
          <PhotoMarkButtons photo={photo} />
        </div>
      )}
      {failed === photo.id && (
        <div className="pf-notice">
          <Icon name="wifiOff" size="1.1rem" />
          This photo can’t be loaded right now.
        </div>
      )}
      {(caption || taken) && (
        <div className="pf-caption">
          {caption && <strong>{caption}</strong>}
          {taken && <span>{taken}</span>}
        </div>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
    </div>
  );
}

export const photoDayWidget: WidgetDefinition<PhotoDayConfig> = {
  type: "photo-day",
  version: 1,
  name: "Photo of the day",
  description: "One family photo a day, with “On this day” memories from earlier years.",
  icon: "sparkle",
  category: "household",
  defaultSize: { w: 3, h: 4 },
  minSize: { w: 2, h: 2 },
  stackHeight: 320,
  defaults: { album: "", showCaption: true },
  settings: [
    { key: "album", label: "Album", type: "text", maxLength: 40, placeholder: "All photos", help: "A folder such as Travel, or a tag such as dog. Leave blank to choose from every wall photo." },
    { key: "showCaption", label: "Show caption", type: "toggle" },
  ],
  provider: "photos",
  capabilities: { requiresAuth: true },
  supportsFullscreen: true,
  chrome: "card",
  Component: PhotoDay,
};
